import { useState } from 'react';
import type { FormEvent, JSX } from 'react';

import { Loader2 } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '../../components/Button/Button';
import { Input } from '../../components/Inputs/Input';
import { Label } from '../../components/Inputs/Label';
import RevvlyMark from '../../components/Layout/revvly-mark.svg?react';
import { useLocalAuth } from '../../components/LocalAuthProvider/LocalAuthProvider';
import { StripeCardForm } from '../../components/StripeCardForm/StripeCardForm';

import styles from './Signup.module.css';
import { confirmSignup, SignupApiError } from './signupApi';
import type { SignupResult } from './signupApi';

/** Where a new owner lands; the onboarding guard takes them on from there. */
const APP_HOME = '/dashboard';

/** Remember the new workspace as the one to open, as choosing it would. */
function selectTenant(tenantId: string): void {
  try {
    localStorage.setItem('selectedTenantId', tenantId);
    sessionStorage.setItem('selectedTenantId', tenantId);
  } catch {
    // Storage may be unavailable; with one workspace the app selects it anyway.
  }
}

/**
 * The page the emailed link opens. Choosing a password here is what creates the
 * workspace, and the person is then signed in with it straight away: no trip
 * back to the sign-in page. If card collection is switched on, the card comes
 * next (signed in already), and skipping it is fine (it can be added later
 * from Billing).
 *
 * An address that already had an account keeps its own password, so the one
 * typed here would not sign it in: that case is sent to sign in, as is any
 * sign-in that fails after the workspace was made.
 */
export default function SignupConfirm(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SignupResult | null>(null);
  const [cardDone, setCardDone] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const { login } = useLocalAuth();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    let made: SignupResult;
    try {
      made = await confirmSignup(token, password);
    } catch (err) {
      setError(err instanceof SignupApiError ? err.message : 'Something went wrong. Try again.');
      setSubmitting(false);
      return;
    }
    if (!made.existingAccount) {
      try {
        await login(made.email, password);
        selectTenant(made.tenantId);
        setSignedIn(true);
        // Straight in, unless a card is asked for first.
        if (!made.card) {
          navigate(APP_HOME, { replace: true });
          return;
        }
      } catch {
        // The workspace exists; signing in by hand still works.
      }
    }
    setResult(made);
    setSubmitting(false);
  };

  const wantsCard = !!result?.card && !cardDone;
  const cardFinished = () => {
    setCardDone(true);
    if (signedIn) navigate(APP_HOME, { replace: true });
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <RevvlyMark className={styles.mark} aria-hidden="true" />

        {!token ? (
          <>
            <h1 className={styles.title}>This link is not valid</h1>
            <div className={styles.actions}>
              <Button variant="primary" onClick={() => navigate('/signup')}>
                Start again
              </Button>
            </div>
          </>
        ) : !result ? (
          <>
            <h1 className={styles.title}>Choose a password</h1>
            <p className={styles.subtitle}>This finishes setting up your workspace.</p>
            <form className={styles.form} onSubmit={submit}>
              <div>
                <Label htmlFor="confirm-password">Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  // Locked once sent: the value is already in state, so nothing is lost.
                  disabled={submitting}
                  required
                  minLength={8}
                  maxLength={128}
                  autoComplete="new-password"
                />
              </div>
              {error && (
                <p className={styles.error} role="alert">
                  {error}
                </p>
              )}
              <Button type="submit" disabled={submitting} aria-busy={submitting}>
                {submitting ? (
                  <span className={styles.busy}>
                    <Loader2 className={styles.spinner} aria-hidden="true" />
                    Setting up your workspace
                  </span>
                ) : (
                  'Create my workspace'
                )}
              </Button>
            </form>
            {error && !submitting && (
              <p className={styles.fine}>
                <Link className={styles.link} to="/signup">
                  Start again
                </Link>
              </p>
            )}
          </>
        ) : wantsCard && result.card ? (
          <>
            <h1 className={styles.title}>Add a card</h1>
            <p className={styles.subtitle}>
              Nothing is charged during your {result.trialDays}-day trial.
            </p>
            <StripeCardForm setup={result.card} onSaved={cardFinished} onCancel={cardFinished} />
          </>
        ) : (
          <>
            <h1 className={styles.title}>Your workspace is ready</h1>
            <p className={styles.subtitle}>
              {result.existingAccount
                ? `Sign in with your existing password for ${result.email}`
                : `Sign in as ${result.email}`}
            </p>
            {!result.card && result.cardUnavailable && (
              <p className={styles.subtitle}>You can add a card later from Settings, Billing.</p>
            )}
            <div className={styles.actions}>
              <Button variant="primary" onClick={() => navigate('/login')}>
                Go to sign in
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
